import React, { SyntheticEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getUserConsentForClient, registerConsent } from '../../actions/consentActions';
import ErrorResponse from '../../components/ErrorResponse';
import PrimaryButton from '../../components/PrimaryButton';
import ViewWrapper, { FormWrapper, Header1 } from '../../components/ViewWrapper';
import { ClientInfo, ErrorType, RedirectAttrs } from '../../models/commonModels';
import { Store } from '../../reducers';
import { RequestType } from '../../reducers/requestReducer';
import { getUrlVars } from '../../utils';
import styles from './Consent.module.scss';

type Props = {
    email: string;
    loading: boolean;
    error?: ErrorType;
    clientInfo?: ClientInfo;
    redirectAttrs: RedirectAttrs;
};

const Consent = ({ email, redirectAttrs, clientInfo, error, loading }: Props): JSX.Element => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const { search } = useLocation();

    useEffect(() => {
        const redirectAttrsInUrl = getUrlVars(search);
        if (
            !loading &&
            !clientInfo &&
            Object.keys(redirectAttrs).length === 0 &&
            Object.keys(redirectAttrsInUrl).length > 0
        ) {
            dispatch(getUserConsentForClient());
        }
    }, [loading, clientInfo, redirectAttrs]);

    // Removing account_device_data_for_google is a temporary fix for Google not wanting us to
    // show the scope name as it apparently is confusing when combined
    // with their desired client description.
    const isOnlyGoogleScope = (scopes: string[]): boolean => {
        return scopes && scopes.length === 1 && scopes[0] === 'account_device_data_for_google';
    };

    const filterScope = (availableScope: string[], queryScope?: string): string[] => {
        if (queryScope === undefined) {
            return availableScope;
        }
        const urlQueryScopes = decodeURIComponent(queryScope).split(' ');
        return urlQueryScopes.every(elem => availableScope.indexOf(elem) > -1) ? urlQueryScopes : [];
    };
    const preselectedScope = (clientInfo?.authScope && filterScope(clientInfo.authScope, redirectAttrs.scope)) || [];
    const disabledClient = clientInfo && !clientInfo.active;
    const onSubmit = (e: SyntheticEvent<HTMLElement>): void => {
        e.preventDefault();
        if (clientInfo && !disabledClient) {
            dispatch(registerConsent({ consent: { scope: clientInfo.authScope }, client: redirectAttrs }));
        }
    };

    const listOfConsents = preselectedScope?.map((scope, i) => {
        const attr = scope.split(':');
        return (
            <li key={i}>
                {txt(attr[0])} {attr[1]}
            </li>
        );
    });

    let invalidClient = undefined;
    if (preselectedScope.length === 0) invalidClient = txt('Consent.InvalidScopeForClient');
    if (disabledClient) invalidClient = txt('Consent.ClientDisabled', { client: clientInfo.name });

    return (
        <ViewWrapper>
            <FormWrapper onSubmit={onSubmit} testAttr={'consent-view'}>
                <Header1>{clientInfo?.name}</Header1>
                <div className={styles.subHeader}>{txt('Consent.LoggedInAs', { email })}</div>
                <div className={styles.paragraph}>{clientInfo?.description}</div>
                {!isOnlyGoogleScope(preselectedScope) && (
                    <div className={styles.listHeader}>{txt('Consent.ClientAskingForPermission')}</div>
                )}
                {!isOnlyGoogleScope(preselectedScope) && <ul className={styles.list}>{listOfConsents}</ul>}

                {error && <ErrorResponse error={`ErrorCodes.${error.error}`} testAttr={'set-consent-error'} />}
                {invalidClient ? (
                    <ErrorResponse error={invalidClient} testAttr={'invalid-client-error'} />
                ) : (
                    <PrimaryButton
                        onClick={onSubmit}
                        testAttr={'login-button'}
                        type="submit"
                        loading={loading}
                        title={'Consent.Agree'}
                    />
                )}
            </FormWrapper>
        </ViewWrapper>
    );
};

const mapStateToProps = (state: Store): Props => {
    const {
        user: { email, redirectAttrs, clientInfo },
        requests: {
            [RequestType.RegisterConsent]: { loading, error },
        },
    } = state;

    return {
        email,
        clientInfo,
        redirectAttrs,
        loading,
        error,
    };
};

export default connect(mapStateToProps)(Consent);
